import React, { useEffect, useState, useMemo } from "react";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from "@mui/material";
import Pagination from "../../common/TablePagination";
import CreateNewButton from "../../common/CreateNewButton";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../common/Loader";
import {
  getFundraiserReferrals,
  getMerchantById,
} from "../../../services/fundraiserService";
import FilterPanel from "../../common/FilterPanel";
import { Dropdown } from "antd";
import SearchBar from "../../common/SearchBar";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import { ReactComponent as FiltersIcon } from "../../../Assets/Icons/filtersIcon.svg";
import { ReactComponent as PendingIcon } from "../../../Assets/Icons/pendingIcon.svg";
import { ReactComponent as AcceptedIcon } from "../../../Assets/Icons/acceptedIcon.svg";

import PopupContent from "./PopupContent";
import DetailsModal from "../../common/DetailsModal";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import debounce from "lodash.debounce";

const FundraiserReferralListTable = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.fundraiserReducer?.loading);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [merchantDetails, setMerchantDetails] = useState({});

  const items = [
    {
      title: "Refer a Merchant",
    },
    {
      title: "Referral List",
    },
  ];

  const columns = [
    {
      name: "No.",
    },
    {
      name: "Name",
    },
    {
      name: "Date",
    },
    {
      name: "Email",
    },
    {
      name: "Contact",
    },
    {
      name: "Status",
    },
  ];

  const [allReferrals, setAllReferrals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [filterParams, setFilterParams] = useState({
    status: null,
    duration: null,
  });

  const getAllReferrals = async (search) => {
    // Create params object with all required parameters
    const params = {
      page: currentPage,
      limit: 10,
      search: search || null,
      status: filterParams.status?.toLowerCase() || null,
      duration: filterParams.duration?.toLowerCase() || null,
    };

    // Remove null values to avoid sending empty parameters
    Object.keys(params).forEach((key) =>
      params[key] === null ? delete params[key] : {}
    );

    const refferralPromise = await dispatch(
      getFundraiserReferrals(params)
    ).unwrap();

    if (refferralPromise?.success === true) {
      // Sort in frontend as a fallback to ensure latest is always on top
      const sortedData = [...(refferralPromise?.data || [])].sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setAllReferrals(sortedData);
      setCurrentPage(refferralPromise?.currentPage);
      setTotalPages(refferralPromise?.totalPages);
      setCount(refferralPromise?.count);
    }
  };

  useEffect(() => {
    getAllReferrals(searchQuery);
  }, [currentPage, filterParams]);

  const fetchMerchantDetails = async (merchantId) => {
    try {
      const merchantDetailsPromise = await dispatch(
        getMerchantById({ id: merchantId })
      )?.unwrap();
      if (merchantDetailsPromise?.success === true) {
        setMerchantDetails(merchantDetailsPromise?.data);
        setIsDetailOpen(true);
      }
    } catch (error) {
      console.error("Error fetching offer details:", error);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOpenChange = (flag) => {
    setOpen(flag);
  };

  const [filterData, setFilterData] = useState([
    {
      id: "status",
      title: "Status",
      options: ["Accepted", "Pending"],
      defaultValue: null,
      multiSelect: false,
    },
    {
      id: "duration",
      title: "Duration",
      options: ["Today", "This Week", "This Month", "This Year"],
      defaultValue: null,
      multiSelect: false,
    },
  ]);

  // Create initial state from filter data
  const createInitialState = (filters) => {
    return filters.reduce((acc, filter) => {
      if (filter.multiSelect) {
        // For multi-select, initialize as array
        acc[filter.id] = Array.isArray(filter.defaultValue)
          ? filter.defaultValue
          : [];
      } else {
        // For single select, initialize as single value
        acc[filter.id] = filter.defaultValue;
      }
      return acc;
    }, {});
  };

  // Initialize selections with the initial state
  const [selections, setSelections] = useState(createInitialState(filterData));

  const handleSelectionChange = (filterId, value) => {
    setSelections((prev) => ({
      ...prev,
      [filterId]: value,
    }));
  };

  // Update the handleReset function
  const handleReset = () => {
    const initialState = createInitialState(filterData);
    setSelections(initialState);

    // Reset filter params to trigger new API call
    setFilterParams({
      status: null,
      duration: null,
    });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        getAllReferrals(query);
      }, 500),
    [filterParams]
  );

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleApply = () => {
    console.log("Applying filters:", selections);

    // Update filter params to trigger new API call
    setFilterParams({
      status: selections.status,
      duration: selections.duration,
    });

    setOpen(false);
  };

  const openPopup = (merchantId) => {
    fetchMerchantDetails(merchantId);
  };

  const closePopup = () => {
    setIsDetailOpen(false);
  };

  return (
    <>
      {loading === true && <Loader />}
      {isDetailOpen && (
        <DetailsModal
          handleClose={closePopup}
          content={<PopupContent merchantDetails={merchantDetails} />}
          popupClassName="p-6 w-[335px] sm:w-[600px] md:w-[728px]"
        />
      )}
      {/* Header Row: Title, Breadcrumbs, Button */}
      <div className="flex flex-col md:flex-row items-center justify-between mt-2 mb-6 gap-4">
        <div className="w-full md:w-auto">
          {/* Mobile Title */}
          <h1 className="md:hidden text-[20px] font-bold text-[#303030] mb-2">Referral List</h1>
          {/* Desktop Breadcrumbs */}
          <div className="hidden md:block">
            <BreadCrumsHeader heading="Referral List" items={items} form={true} />
          </div>
        </div>
        <div className="w-full md:w-auto flex justify-end">
          <CreateNewButton
            text={"Refer New Merchant"}
            navigateTo={"/fundraiser/refer-merchant"}
            // Removed fixed left positioning for icon to let it flow/center naturally if the component supports it, 
            // but since CreateNewButton uses absolute, we adjust it:
            iconClassName="left-4 top-1/2 transform -translate-y-1/2 w-4 h-4"
            // Added padding to button to ensure text doesn't overlap icon
            className="w-full md:w-auto pl-10"
          />
        </div>
      </div>

      {/* Search and Filter Row */}
      <div className="w-full flex flex-row justify-between items-center mt-2 mb-4 gap-3">
        <SearchBar
          parentDivClass="w-full md:w-[400px]"
          divClassName="relative w-full"
          inputClassName="searchBar w-full p-3 pr-10 text-[14px] font-poppins font-[400] outline-none border border-gray-300 rounded-[5px]"
          // Fixed icon positioning: vertically centered, pinned to right
          iconClassName="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
          placeholder="Search..."
          value={searchQuery}
          handleChange={(e) => handleSearchChange(e.target.value)}
        />
        {/* Filter Dropdown */}
        <Dropdown
          overlay={
            <FilterPanel
              filterData={filterData}
              selections={selections}
              handleApply={handleApply}
              handleCancel={handleCancel}
              handleReset={handleReset}
              handleSelectionChange={handleSelectionChange}
            />
          }
          trigger={["click"]}
          open={open}
          onOpenChange={handleOpenChange}
          placement="bottomRight"
          overlayStyle={{
            minWidth: window.innerWidth < 768 ? "90vw" : "320px", // Wider on mobile
            width: window.innerWidth < 768 ? "90vw" : "auto",
            maxHeight: "80vh",
            zIndex: 1000,
            marginTop: "10px", // Pushes it slightly down for better separation
          }}
          overlayClassName="filter-dropdown-overlay"
        >
          {/* Compact Filter Button for Mobile */}
          <button className="flex justify-center items-center gap-2 border border-[#00ADEF] rounded-md p-2 md:p-3 h-[45px] w-auto bg-white min-w-[45px] md:min-w-[120px]">
            <FiltersIcon className="w-[20px] h-[20px] filter-blue" style={{ filter: "invert(58%) sepia(91%) saturate(2311%) hue-rotate(167deg) brightness(98%) contrast(101%)" }} />
            <span className="hidden md:inline font-[600] text-[14px] font-poppins leading-[22px] text-[#00ADEF]">
              Filters
            </span>
            <DownArrow className="hidden md:block" style={{ filter: "invert(58%) sepia(91%) saturate(2311%) hue-rotate(167deg) brightness(98%) contrast(101%)" }} />
          </button>
        </Dropdown>
      </div>

      <div className="col-span-12 mt-4">
        {/* Mobile Card View */}
        <div className="md:hidden flex flex-col gap-4 mb-4">
          {allReferrals?.map((row, index) => (
            <div key={index} className="bg-white p-4 rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.10)] flex flex-col gap-3 border border-[#E0E0E0]">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-[14px] font-[600] text-[#303030]">{row?.merchant?.businessName || row?.name || "-"}</p>
                  <p className="text-[12px] text-[#8C8C8C] break-all">{row?.user?.email || row?.email || "-"}</p>
                </div>
                <div className="min-w-[50px] flex justify-end">
                  {row?.status == "accepted" ? <AcceptedIcon /> : <PendingIcon />}
                </div>
              </div>

              <div className="flex justify-between items-center text-[12px] font-[500] text-[#303030]">
                <span>Date: {separateDateTimeAndConvertToLocale(row?.createdAt)?.localDate || "-"}</span>
                <span>Contact: {row?.merchant?.contactPersonNumber || row?.contactNumber || "-"}</span>
              </div>


            </div>
          ))}
          {allReferrals?.length === 0 && !loading && (
            <div className="text-center py-4 text-[#8C8C8C]">No referrals found.</div>
          )}
        </div>

        <TableContainer className="hidden md:block" style={{ width: "100%", overflowX: "auto" }}>
          <Table
            sx={{
              minWidth: "1000px",
              borderCollapse: "separate",
              borderSpacing: "0px 8px",
            }}
            aria-label="simple table"
          >
            <TableHead className="bg-[#00ADEF]">
              <TableRow
                className="h-[50px]"
                sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
              >
                {columns.map((column) => {
                  return <TableCell key={column.name} className="!text-[#FFFFFF]">{column.name}</TableCell>;
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {allReferrals?.map((row, index) => {
                return (
                  <TableRow
                    className="h-[50px]"
                    sx={{
                      boxShadow: "0px 4px 4px 0px #00000040",
                      backgroundColor:
                        (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                    }}
                    key={index}
                  >
                    <TableCell className="max-w-[50px]">{index + 1}</TableCell>
                    <TableCell
                      // title={row?.merchant?.businessName || ""}
                      title={row?.merchant?.businessName || row?.name || "-"}
                      className="max-w-[50px] truncate"
                    >
                      {row?.merchant?.businessName || row?.name || "-"}
                    </TableCell>
                    <TableCell
                      title={row?.createdAt || ""}
                      className="max-w-[150px] truncate"
                    >
                      {separateDateTimeAndConvertToLocale(row?.createdAt)
                        ?.localDate || "-"}
                    </TableCell>
                    <TableCell
                      title={row?.user?.email || row?.email || "-"}
                      className="max-w-[100px] truncate"
                    >
                      {row?.user?.email || row?.email || "-"}
                    </TableCell>
                    <TableCell
                      title={
                        row?.merchant?.contactPersonNumber ||
                        row?.contactNumber ||
                        "-"
                      }
                      className="max-w-[70px] truncate"
                    >
                      {row?.merchant?.contactPersonNumber ||
                        row?.contactNumber ||
                        "-"}
                    </TableCell>
                    <TableCell
                      title={row?.status || ""}
                      className="max-w-[50px] truncate"
                    >
                      {row?.status == "accepted" ? (
                        <AcceptedIcon />
                      ) : (
                        <PendingIcon />
                      )}
                    </TableCell>

                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Pagination
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
      </div>
    </>
  );
};

export default FundraiserReferralListTable;
